import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { buildDataTableQueryOptions, ensureRouteQueriesData } from "@/lib/route-loader";
import { dataTableFiltersSearchSchema } from "@/lib/route-search";
import { Gender, type AdminStudentListItemDTO } from "@/orval/model";
import {
  getAdminStudentsGetAdminStudentFilterConfigQueryOptions,
  getAdminStudentsGetAdminStudentFilterConfigQueryKey,
  useAdminStudentsAddChildAsStudent,
  adminStudentsGetAdminStudentFilterConfig,
  adminStudentsGetAdminStudentsForOrganization,
} from "@/orval/admin-students";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import type { CustomColumnDef } from "@/types/custom-column-def";
import { convertGradeLevelToString } from "@/lib/string-utiils";
import { findEnumName } from "@/lib/enum-utils";
import { PlusIcon, UploadIcon, DownloadIcon, ArrowUpRight } from "lucide-react";
import { IconButton } from "@/components/ui/icon-button";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { OrganizationStudentSiblingComboboxFormField } from "@/components/form/form-field/combobox/organization-student-sibling-combobox-form-field";
import { Form } from "@/components/form/form";
import { ImportStudentsDialog } from "./_students/-components/import-students-dialog";
import { EditStudentDialog } from "./_students/-components/edit-student-dialog";
import { ExportStudentsDialog } from "./_students/-components/export-students-dialog";
import { PortalPageHeader } from "@/components/portal-page-header";

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/students")({
  validateSearch: dataTableFiltersSearchSchema,
  loader: ({ params, location, ...loaderContext }) => {
    const { searchString, filters } = dataTableFiltersSearchSchema.parse(location.search);
    const queryOptions = buildDataTableQueryOptions({ searchString });

    return ensureRouteQueriesData(loaderContext, [
      getAdminStudentsGetAdminStudentFilterConfigQueryOptions(params.organizationId),
      {
        queryKey: ["admin-students", params.organizationId, queryOptions, filters],
        queryFn: () => adminStudentsGetAdminStudentsForOrganization(params.organizationId, filters ?? [], queryOptions),
      },
    ]);
  },
  component: StudentsAdminPage,
});

function StudentsAdminPage() {
  const { organizationId } = Route.useParams();

  const getStudents = adminStudentsGetAdminStudentsForOrganization;
  const getStudentFiltersConfig = adminStudentsGetAdminStudentFilterConfig;
  const { mutateAsync: addSiblingToOrganization, isPending: isAddingChild } = useAdminStudentsAddChildAsStudent();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<number | null>(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const studentsColumns: CustomColumnDef<AdminStudentListItemDTO>[] = [
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title="First Name" />,
      accessorKey: "firstName",
      sortField: "Child.FirstName",
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title="Last Name" />,
      accessorKey: "lastName",
      sortField: "Child.LastName",
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title="Gender" />,
      accessorFn: (row) => findEnumName(Gender, row.gender),
      id: "gender",
      sortField: "Child.Gender",
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title="Age" />,
      accessorKey: "age",
      sortField: "Child.Age",
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title="Grade Level" />,
      accessorFn: (row) => convertGradeLevelToString(row.gradeLevel),
      id: "gradeLevel",
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date of Birth" />,
      accessorFn: (row) => (row.dateOfBirth ? format(parseISO(row.dateOfBirth), "MM/dd/yyyy") : "N/A"),
      id: "dateOfBirth",
      sortField: "Child.Birthday",
    },
    {
      header: "Additional Info / Allergies",
      id: "additionalInformation",
      cell: ({ row }) => {
        const value = row.original.additionalInformation?.trim();

        return value ? (
          <span className="block max-w-[18rem] truncate text-sm text-foreground" title={value}>
            {value}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      header: "Family",
      id: "family",
      cell: ({ row }) => {
        const family = row.original.familyName;

        return (
          <Link
            to={"/organizations/$organizationId/admin/families/$familyId"}
            search={{ tab: "payments" }}
            params={{ organizationId, familyId: row.original.familyId }}
            className="underline flex gap-1 items-center"
          >
            {family}
            <ArrowUpRight className="inline-block size-4" />
          </Link>
        );
      },
    },
  ];

  const handleAddSibling = async (data: { childId: number }) => {
    if (data.childId) {
      await addSiblingToOrganization({ organizationId, childId: data.childId });
      setIsDialogOpen(false);
    }
  };

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title="Students"
        actions={
          <>
            <IconButton
              variant="outline"
              className="flex-1 md:flex-none"
              onClick={() => setIsExportDialogOpen(true)}
              icon={<DownloadIcon />}
            >
              Export Students
            </IconButton>

            <IconButton
              variant="accent"
              className="flex-1 md:flex-none"
              onClick={() => setIsImportDialogOpen(true)}
              icon={<UploadIcon />}
            >
              Import Students
            </IconButton>

            <IconButton className="flex-1 md:flex-none" onClick={() => setIsDialogOpen(true)} icon={<PlusIcon />}>
              Add Sibling
            </IconButton>
          </>
        }
      />

      <DataTable
        columns={studentsColumns}
        queryFn={(queryOptions, signal, filters) => getStudents(organizationId, filters ?? [], queryOptions, signal)}
        queryKeyFn={(params, filters) => ["admin-students", organizationId, params, filters]}
        filtersQueryFn={(signal) => getStudentFiltersConfig(organizationId, signal)}
        filtersQueryKeyFn={() => getAdminStudentsGetAdminStudentFilterConfigQueryKey(organizationId)}
        onRowClick={(row) => setSelectedStudent(row.id)}
        getRowIdFn={(row) => row.id.toString()}
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select a Sibling</DialogTitle>
          </DialogHeader>
          <Form<{ childId: number }> onSubmit={handleAddSibling}>
            <OrganizationStudentSiblingComboboxFormField
              organizationId={organizationId}
              name="childId"
              rules={{ required: true }}
            />
            <DialogFooter className="flex-col sm:flex-row">
              <Button disabled={isAddingChild}>Add Sibling</Button>
              <Button variant="secondary" onClick={() => setIsDialogOpen(false)} disabled={isAddingChild}>
                Cancel
              </Button>
            </DialogFooter>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Student Dialog */}
      {selectedStudent && (
        <EditStudentDialog
          organizationId={organizationId}
          studentId={selectedStudent}
          isOpen={!!selectedStudent}
          onClose={() => setSelectedStudent(null)}
        />
      )}

      {/* Import Students Dialog */}
      <ImportStudentsDialog
        organizationId={organizationId}
        isOpen={isImportDialogOpen}
        setIsOpen={setIsImportDialogOpen}
      />

      {/* Export Students Dialog */}
      <ExportStudentsDialog
        organizationId={organizationId}
        isOpen={isExportDialogOpen}
        setIsOpen={setIsExportDialogOpen}
      />
    </div>
  );
}
